import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, LOCALE_ID, NgModule, provideZoneChangeDetection } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';
import et from '@angular/common/locales/et';
import fr from '@angular/common/locales/fr';
import { CorePipesModule, HedaCoreUtilModule } from '@heda/core-util';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TerminologyModule } from 'app/mdm/terminology/terminology.module';
import { StorageServiceModule } from 'ngx-webstorage-service';
import { ClipboardModule } from 'ngx-clipboard';
import {
  AfterLoginHandler,
  AuthContext,
  CoreZorroModule,
  ErrorHandler,
  HedaCoreHospitalUiModule,
  LangInterceptor,
  MenuHandler,
  TelemedecineModule,
  WebVersionInterceptor,
  YupiHttpInterceptor,
} from '@heda/core-hospital-ui';
import { IntegrationModule } from 'app/integration/integration.module';
import { CryptoModule } from 'app/crypto/crypto.module';
import { PechkinModule } from 'app/pechkin/pechkin.module';
import { HedaAuthHandler } from 'app/integration/heda-auth-handler.service';
import { EnvParams } from 'app/core/env-params.service';
import { OauthHttpInterceptor } from '@heda/core-util/oauth/oauth-http-interceptor';
import { HedaCoreUiEnvironment } from '@heda/core-hospital-ui/heda-core-ui-environment';
import { environment } from 'environments/environment';
import { EsterMenuHandler, UserInfoMenuHandler } from 'app/core/menu-handlers';
import { IModuleTranslationOptions, ModuleTranslateLoader } from '@larscom/ngx-translate-module-loader';
import { et_EE, NZ_DATE_CONFIG, NZ_I18N } from 'ng-zorro-antd/i18n';
import { HotkeyModule } from 'angular2-hotkeys';
import { AngularDeviceInformationService } from 'angular-device-information';

registerLocaleData(en);
registerLocaleData(et);
registerLocaleData(fr);

export function preloadAuth(authContext: AuthContext): () => Promise<any> {
  return () => authContext.refresh();
}

export function initSystemParams(sysParams: EnvParams): () => Promise<any> {
  return () => sysParams.init().toPromise();
}

export function setLocale(locale: string): () => void {
  return () => localStorage.setItem('locale', locale);
}

export function webVersionInterceptor(env: HedaCoreUiEnvironment): WebVersionInterceptor {
  return new WebVersionInterceptor(
    [
      environment.accountingApi,
      environment.receptionApi,
      environment.umkaApi,
      environment.mdmApi,
      environment.mpiApi,
      environment.estnhsApi,
      environment.procmanApi,
      environment.treatmentApi,
      environment.pechkinApi,
      environment.sauronApi,
    ],
    env
  );
}

export function rootTranslateLoader(http: HttpClient): ModuleTranslateLoader {
  const options: IModuleTranslationOptions = {
    // enableNamespacing: false,
    modules: [
      { baseTranslateUrl: './assets/core-util/i18n' },
      { baseTranslateUrl: './assets/core-hospital-ui/i18n' },
      { baseTranslateUrl: './assets/i18n' },
      { baseTranslateUrl: './assets/i18n/schedule' },
      { baseTranslateUrl: './assets/i18n/procman' },
      { baseTranslateUrl: './assets/i18n/reception' },
      { baseTranslateUrl: './assets/i18n/accounting' },
      { baseTranslateUrl: './assets/i18n/treatment' },
      { baseTranslateUrl: './assets/i18n/sauron' },
      { baseTranslateUrl: './assets/i18n/study-device' },
    ],
  };
  //TODO: cache hack
  return new ModuleTranslateLoader(http, options);
}

@NgModule({ declarations: [AppComponent],
    bootstrap: [AppComponent], imports: [BrowserModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        FormsModule,
        StorageServiceModule,
        ClipboardModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: rootTranslateLoader,
                deps: [HttpClient],
            },
        }),
        CoreZorroModule,
        TelemedecineModule,
        HedaCoreUtilModule,
        HedaCoreHospitalUiModule,
        CorePipesModule,
        TerminologyModule,
        PechkinModule,
        IntegrationModule,
        CryptoModule,
        HotkeyModule.forRoot()], providers: [
        { provide: NZ_I18N, useValue: et_EE },
        { provide: LOCALE_ID, useValue: 'et' },
        { provide: HedaCoreUiEnvironment, useValue: { ...environment, isOauthEnabled: !environment.yupiEnabled } },
        { provide: MenuHandler, useClass: UserInfoMenuHandler, multi: true },
        { provide: MenuHandler, useClass: EsterMenuHandler, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: YupiHttpInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: OauthHttpInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: LangInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useFactory: webVersionInterceptor, deps: [HedaCoreUiEnvironment], multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorHandler, multi: true },
        { provide: NZ_DATE_CONFIG, useValue: { firstDayOfWeek: 1 } },
        { provide: APP_INITIALIZER, useFactory: preloadAuth, deps: [AuthContext], multi: true },
        { provide: APP_INITIALIZER, useFactory: initSystemParams, deps: [EnvParams], multi: true },
        { provide: APP_INITIALIZER, useFactory: setLocale, deps: [LOCALE_ID], multi: true },
        { provide: AfterLoginHandler, useClass: HedaAuthHandler },
        AngularDeviceInformationService,
        provideHttpClient(withInterceptorsFromDi()),
        provideZoneChangeDetection({ eventCoalescing: true })
    ] })
export class AppModule {}
